'use client'
import { useCallback, useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import { getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'
import { getSanitizedRedirectUrls, isOAuthCallbackOnUrl, LOGIN_STATUS_MESSAGE, SESSION_EXPIRED_KEY } from './utils'
import AuthWrapper from '../../components/Auth/AuthWrapper'
import { captureException } from '../../components/Sentry/utils'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import useLoginUser from '../../hooks/useLoginUser'
import { LoginVariantA } from '../LogIn/Variants/ExperimentA'
import mixpanelCl from '@src/telemetry/integrations/mixpanel'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackClickEvent, trackEvent } from '@src/telemetry/track'
import ForgotPasswordModal from '../../components/ForgotPasswordModal'
import { useRedirectToCheckoutIfApplicable } from './hooks/use-login-redirect'
import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { useLoginSideEffectsAndRedirect } from './hooks/use-login-sideeffects'
import {
  resendSignUpCode,
  createAuthHubListener,
  handleOAuthCompletion,
  useAuth,
} from '@lastbrand/fe-common/universal-auth'
import { useTranslation } from 'react-i18next'
import { getStandardLocaleFromCookie } from '@src/utils/i18n.utils'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { clearStaleLogoutBroadcastForSignIn } from '@src/utils/cross-domain-auth.helper'
import useDeviceInfo from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { getAuthSideImage } from './hooks/get-auth-side-image'
import { useIsB2bAuthSource } from './hooks/use-is-b2b-auth-source'

const StyledLogin = styled.div``

// isB2BAuthSource: SSR-computed on the page (query param, or the _oauth_source cookie on the
// Cognito callback) — the only B2B signal that is correct on the server *and* stable for the
// whole page load, unlike sessionStorage which SSR can't see and cleanup wipes pre-redirect.
const LogIn = ({ location, data, isB2BAuthSource = false }) => {
  const router = useRouter()
  const { t } = useTranslation()
  const { t: tAccountPage } = useTranslation('accountPage')
  const redirectToCheckoutIfApplicable = useRedirectToCheckoutIfApplicable()
  const oauthCompletedRef = useRef(false)

  const page = data.authenticationPage
  const { deviceType } = useDeviceInfo()
  // Same SSR-safe, effect-latched signal the form copy uses — a render-time
  // sessionStorage read here caused the image to flash D2C on the OAuth callback.
  // Also drives AuthWrapper's isB2b hero-image styling from the base branch.
  const isB2bSource = useIsB2bAuthSource(isB2BAuthSource)
  const authSideImage = getAuthSideImage({
    page,
    isB2bSource,
    deviceType,
  })
  const { loginUser, onGoogleFederatedLogIn } = useLoginUser()
  const [serverError, setServerError] = useState()

  const loginDownStreamSideEffectsWithRedirect = useLoginSideEffectsAndRedirect(setServerError)

  const [unverifiedAccount, setUnverifiedAccount] = useState(false)
  const [verificationEmailSent, setVerificationEmailSent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState()
  const [resetPasswordModalState, setResetPasswordModalState] = useState(false)
  const params = new URLSearchParams(location.search)
  const resetPwPopup = params.get('reset-pw-popup')
  const resetPassword = params.get('reset-password')
  const sessionExpired = params.get('status') === SESSION_EXPIRED_KEY
  const isInResetPasswordFlow = resetPassword === 'true'
  const oauthRefLoading = useRef(false)
  // Google sign-in redirects back to /log-in, which would otherwise look like an
  // already-signed-in mount — check for `?code=` to flip this to false in that case.
  const isAlreadySignedInRef = useRef(!isOAuthCallbackOnUrl())
  const { isLoggedIn: isLoggedInAsync, isInitializing } = useAuth()

  useEffect(() => {
    if (isInResetPasswordFlow || isInitializing) return
    const isLoggedInSync = AuthUtil.isLoggedIn()
    // After Google OAuth, AuthUtil.isLoggedIn() (sync localStorage check) may lag behind
    // isLoggedInAsync (Zustand, set by initializeAuth). Use either for the logged-in guard.
    const isLoggedIn = isLoggedInSync || isLoggedInAsync

    if (!isLoggedIn) {
      isAlreadySignedInRef.current = false
      return
    }

    if (!oauthRefLoading.current) {
      if (isLoggedInAsync) {
        setLoading(true)
        // URL params may not have redirecturi after OAuth round-trip (?code=... replaces original params).
        // Fall back to sessionStorage where GoogleSignIn.tsx saved the B2B return URL before redirect.
        let redirectUri = getSanitizedRedirectUrls()
        if (!redirectUri) {
          const storedRedirectUri = sessionStorage.getItem('redirecturi')
          if (storedRedirectUri) {
            redirectUri = JSON.parse(storedRedirectUri)
            sessionStorage.removeItem('redirecturi')
          }
        }

        redirectToCheckoutIfApplicable(redirectUri, { isAlreadySignedIn: isAlreadySignedInRef.current })
        setLoading(false)
      } else {
        window.logoutUser?.(false, false, true)
      }
    }
  }, [isInResetPasswordFlow, isLoggedInAsync, isInitializing, redirectToCheckoutIfApplicable])

  useEffect(() => {
    if (sessionExpired) {
      setServerError({ message: LOGIN_STATUS_MESSAGE[SESSION_EXPIRED_KEY] })
    }
  }, [sessionExpired])

  useEffect(() => {
    if (resetPwPopup === 'true') {
      setResetPasswordModalState(true)
    }
  }, [resetPwPopup])

  const resendVerificationEmail = async email => {
    try {
      const standardLocale = getStandardLocaleFromCookie()
      const shouldIncludeLocale = typeof window !== 'undefined' && window.__debug?.isI18nEnabled && standardLocale
      await resendSignUpCode(email?.toLowerCase(), {
        ...(shouldIncludeLocale
          ? {
              locale: standardLocale,
            }
          : {}),
      })
      trackClickEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, {
        trackInternal: true,
        // @i18n-ignore
        category: 'login',
        action: 'resend_verification',
        context: {
          login_email: email,
          lb_user_id: getlbUserId(),
        },
      })
    } catch (err) {
      captureException(err, {
        module: MODULES.UNIVERSAL_AUTH,
        feature: FEATURES.UNIVERSAL_AUTH,
        additionalTags: {
          // @i18n-ignore
          login_method: 'email',
          // @i18n-ignore
          user_type: 'd2c',
          error_type: err?.code || err?.name || 'resend_verification_email_failed',
        },
      })
      trackEvent(TELEMETRY_EVENT_NAMES.API_RESPONSE, {
        apiName: 'resendVerificationEmail',
        status: `failure`,
        context: {
          email: email,
          error: err.message,
          errorType: err.name,
        },
      })
    }
    setUnverifiedAccount(false)
    setVerificationEmailSent(true)
    setTimeout(() => {
      setVerificationEmailSent(false)
    }, 10000)
  }

  const backToLogin = () => {
    setServerError('')
  }

  const onGoogleLogIn = useCallback(
    async (user, callbackFn) => {
      setServerError('')
      setLoading(true)
      try {
        // Get the correct email from user object
        const userEmail = user?.attributes?.email || user?.username || user?.email

        setEmail(userEmail) // Set email for the component state
        await onGoogleFederatedLogIn(user)
        trackEvent(TELEMETRY_EVENT_NAMES.FORM_SUBMIT, {
          trackInternal: true,
          // @i18n-ignore
          category: 'login',
          action: 'account_login',
          context: {
            login_email: userEmail,
            lb_user_id: getlbUserId(),
            // @i18n-ignore
            login_method: 'SSO',
          },
        })
        mixpanelCl.identify({ email: userEmail, isTentativeEmail: false })
        let redirectUri = getSanitizedRedirectUrls()
        const storedRedirectUri = sessionStorage.getItem('redirecturi')
        if (storedRedirectUri) {
          redirectUri = JSON.parse(storedRedirectUri)
          sessionStorage.removeItem('redirecturi')
        }

        await loginDownStreamSideEffectsWithRedirect(redirectUri)
        setLoading(false)
        callbackFn()
      } catch (err) {
        captureException(err, {
          module: MODULES.UNIVERSAL_AUTH,
          feature: FEATURES.UNIVERSAL_AUTH,
          additionalTags: {
            // @i18n-ignore
            login_method: 'google',
            // @i18n-ignore
            user_type: 'd2c',
            error_type: err?.code || err?.name || 'google_login_failed',
          },
        })
        if (err && err.code == 'PASSWORD_RESET_NEEDED') {
          router.push('?reset-pw-popup=true')
        }
        setLoading(false)
        setServerError(err)
        callbackFn()
      }
    },
    [onGoogleFederatedLogIn, loginDownStreamSideEffectsWithRedirect, data, router],
  )

  // Handle OAuth completion events
  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const unsubscribe = createAuthHubListener(async ({ event }) => {
      if (event === 'signInWithRedirect') {
        oauthRefLoading.current = true

        if (oauthCompletedRef.current || loading) {
          return
        }

        try {
          const user = await handleOAuthCompletion()

          if (user && user.attributes && user.attributes.email) {
            await onGoogleLogIn(user, () => {
              console.info(`Google OAuth login completed`)
            })
            oauthCompletedRef.current = true // Mark as completed
          }
        } catch (error) {
          captureException(error, {
            module: MODULES.UNIVERSAL_AUTH,
            feature: FEATURES.UNIVERSAL_AUTH,
            additionalTags: {
              // @i18n-ignore
              login_method: 'google',
              // @i18n-ignore
              user_type: 'd2c',
              error_type: error?.code || 'oauth_completion_failed',
            },
          })
          setServerError(error)
          oauthCompletedRef.current = false
        }
        oauthRefLoading.current = false
      }
    })

    return () => {
      // Reset the OAuth completion flag when component unmounts
      oauthCompletedRef.current = false
      return unsubscribe()
    }
  }, [onGoogleLogIn, loading])

  const openResetPasswordModal = email => {
    router.replace('?reset-pw-popup=true')
    setEmail(email)
  }

  const login = async data => {
    let { username, password, hasPasswordResetted = false } = data
    setServerError('')
    setLoading(true)

    clearStaleLogoutBroadcastForSignIn()

    if (username) {
      username = username?.toLowerCase()
    }
    setEmail(username)

    try {
      // Get customer access token
      // Sign in

      await loginUser(username, password, hasPasswordResetted)
      trackEvent(TELEMETRY_EVENT_NAMES.FORM_SUBMIT, {
        trackInternal: true,
        // @i18n-ignore
        category: 'login',
        action: 'account_login',
        context: {
          login_email: username,
          lb_user_id: getlbUserId(),
          // @i18n-ignore
          login_method: 'general',
        },
      })
      mixpanelCl.identify({ email: username, isTentativeEmail: false })
      let redirectUri = getSanitizedRedirectUrls()
      const storedRedirectUri = sessionStorage.getItem('redirecturi')
      if (storedRedirectUri) {
        redirectUri = JSON.parse(storedRedirectUri)
        sessionStorage.removeItem('redirecturi')
      }
      await loginDownStreamSideEffectsWithRedirect(redirectUri)
    } catch (err) {
      setLoading(false)

      // Log every login failure to Datadog (searchable via login_method / user_type / error_type),
      // including expected user errors like wrong password / user-not-found.
      captureException(err, {
        module: MODULES.UNIVERSAL_AUTH,
        feature: FEATURES.UNIVERSAL_AUTH,
        additionalTags: {
          // @i18n-ignore
          login_method: 'email',
          // @i18n-ignore
          user_type: 'd2c',
          error_type: err?.code || err?.name || 'email_login_failed',
        },
      })

      const errorCodesToIgnore = new Set(['UserNotFoundException', 'NotAuthorizedException'])

      /** Analytics failure event is intentionally skipped for these expected user errors. */
      if (!errorCodesToIgnore.has(err.code)) {
        trackEvent(TELEMETRY_EVENT_NAMES.API_RESPONSE, {
          apiName: `login`,
          status: `failure`,
          context: {
            email: email,
            error: err.message,
            errorType: err.code || err.name,
          },
        })
      }

      if (err && err.code == 'UserNotFoundException') {
        err.message =
          'Hmm. We don\'t have that email on record. <br/> Please verify your email or <a href="/sign-up" style="text-decoration:underline;font-weight: 500">sign up</a> if your account does not exist.'
      } else if (err && err.code == 'UserNotConfirmedException') {
        try {
          setUnverifiedAccount(true)
          err.message = t('account_verification_required')
        } catch (err) {
          err.message = t('email_verification_resend_error')
          captureException(err, {
            module: MODULES.UNIVERSAL_AUTH,
            feature: FEATURES.UNIVERSAL_AUTH,
            additionalTags: {
              // @i18n-ignore
              login_method: 'email',
              // @i18n-ignore
              user_type: 'd2c',
              // @i18n-ignore
              error_type: 'resend_verification_email_failed',
            },
          })
        }
      } else if (err && err.code == 'PASSWORD_RESET_NEEDED') {
        router.push('?reset-pw-popup=true')
      } else if (err && err.code == 'InternalErrorException') {
        err.message = t('login_error_try_later')
      } else if (err && err.code == 'PasswordResetRequiredException') {
        err.message = t('login_failed_reset_password')
      } else if (err && err.code == 'NotAuthorizedException') {
        if (err.message.includes('attempts exceeded')) {
          err.message = tAccountPage('password_attempts_exceeded')
        } else {
          err.message = tAccountPage('incorrectPwd')
        }
      }
      setServerError(err)
    }
  }

  const onCloseForgotPassword = () => {
    router.replace('?reset-pw-popup=false')
    setResetPasswordModalState(false)
  }

  const forgotPasswordSubmitCallback = async (username, password) => {
    setResetPasswordModalState(false)
    await login({ username, password, hasPasswordResetted: true })
  }

  return (
    <>
      <StyledLogin>
        <AuthWrapper image={authSideImage} classNames="auth_wrapper_login">
          <LoginVariantA
            isB2BAuthSource={isB2BAuthSource}
            serverError={serverError}
            unverifiedAccount={unverifiedAccount}
            resendVerificationEmail={resendVerificationEmail}
            verificationEmailSent={verificationEmailSent}
            email={email}
            backToLogin={backToLogin}
            page={page}
            login={login}
            loading={loading}
            openResetPasswordModal={openResetPasswordModal}
            isInResetPasswordFlow={isInResetPasswordFlow}
          />
          <ForgotPasswordModal
            open={resetPasswordModalState}
            username={email}
            onClose={onCloseForgotPassword}
            onSubmitCallback={forgotPasswordSubmitCallback}
          />
        </AuthWrapper>
      </StyledLogin>
    </>
  )
}

export default LogIn
